/**
 * Supported file formats for upload.
 */

export const AUDIO_FORMATS = [
  "mp3",
  "wav",
  "ogg",
  "webm",
  "m4a",
  "flac",
  "m4p",
  "aac",
  "mpeg",
  "amr",
] as const;

export const VIDEO_FORMATS = [
  "mp4",
  "wmv",
  "avi",
  "m4v",
  "mov",
  "flv",
] as const;

export const TEXT_FORMATS = ["txt", "docx", "pdf"] as const;

export type AudioFormat = (typeof AUDIO_FORMATS)[number];
export type VideoFormat = (typeof VIDEO_FORMATS)[number];
export type TextFormat = (typeof TEXT_FORMATS)[number];
export type SupportedFormat = AudioFormat | VideoFormat | TextFormat;

/**
 * MIME type accept list for file inputs / dropzones.
 */
export const ACCEPT_MIME_TYPES = [
  // Audio
  "audio/mp3",
  "audio/wav",
  "audio/ogg",
  "audio/webm",
  "audio/x-m4a",
  "audio/m4a",
  "audio/flac",
  "audio/x-m4p",
  "audio/aac",
  "audio/mpeg",
  "audio/amr",
  // Video
  "video/mp4",
  "video/x-ms-wmv",
  "video/avi",
  "video/x-m4v",
  "video/quicktime",
  "video/x-flv",
  // Text
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
  // Images (for OCR)
  "image/png",
  "image/jpeg",
] as const;

export const ACCEPT_STRING = ACCEPT_MIME_TYPES.join(", ");

/**
 * Map MIME type to canonical extension.
 * Used when the file extension is ambiguous or missing.
 */
export const MIME_TO_EXTENSION: Record<string, string> = {
  "audio/mp3": "mp3",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/ogg": "ogg",
  "audio/webm": "webm",
  "audio/x-m4a": "m4a",
  "audio/m4a": "m4a",
  "audio/flac": "flac",
  "audio/x-m4p": "m4p",
  "audio/aac": "aac",
  "audio/amr": "amr",
  "video/mp4": "mp4",
  "video/x-ms-wmv": "wmv",
  "video/avi": "avi",
  "video/x-m4v": "m4v",
  "video/quicktime": "mov",
  "video/x-flv": "flv",
  "text/plain": "txt",
  "text/html": "txt",
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "image/png": "png",
  "image/jpeg": "jpg",
};

/**
 * Human-readable format hint for the upload UI.
 */
export const FORMAT_HINT = `Supported formats: ${AUDIO_FORMATS.join(", ")}, ${VIDEO_FORMATS.join(", ")}, ${TEXT_FORMATS.join(", ")}`;

/** Max file duration: 10 hours in seconds */
export const MAX_DURATION_SECONDS = 10 * 60 * 60;

/** Free plan file size limit: 2 GB */
export const FREE_PLAN_MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;

/** URL import max size: 200 MB */
export const URL_IMPORT_MAX_SIZE = 200 * 1024 * 1024;

/** Per-file PDF size cap (50 MB). Larger files are rejected at validation. */
export const PDF_MAX_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Determine the media type category from a MIME type string.
 */
export function getMediaCategory(
  mimeType: string,
): "audio" | "video" | "text" | null {
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("video/")) return "video";
  if (
    mimeType.startsWith("text/") ||
    mimeType === "application/pdf" ||
    mimeType.includes("wordprocessingml")
  )
    return "text";
  if (mimeType.startsWith("image/")) return "text"; // images treated as text/OCR
  return null;
}

/**
 * Check if a MIME type is a supported format.
 */
export function isSupportedFormat(mimeType: string): boolean {
  return getMediaCategory(mimeType) !== null;
}
